// E23-t2 (first slice) — USAGE, ORG-WIDE.   route: /:org/billing
//
// Contract: vault/Apps/Forge/Pages/Billing.md — f1 (the rollup: one usage read
//   per KB, a failed read degrades to its own unknown row and partial sums say
//   they are partial), f2 (storage by KB as a bar chart, the cap drawn from the
//   ANSWER's own limits and never a client constant), f4 (what is not built is
//   stated on the surface, naming the E23 ticket that builds it — a meter that
//   cannot be read is absent, not zero).
// Contract: vault/Backlog/E23 Usage Metering and Billing.md — t2 is the whole
//   screen of the adopted org-workspace design; THIS FILE IS ITS FIRST SLICE,
//   the part the control plane can already answer for. The plan card, the
//   meters-over-time and the invoices table arrive with t1/t3/t4 and are
//   deliberately absent here rather than mocked: q1–q4's verdicts shape them,
//   and a rendered guess would be a promise this tier has not priced.
// Contract: vault/Apps/Forge/Pages/Home.md q2 lineage — the per-KB usage read
//   and its degrade rule are the SAME ones the Home cards hold; this page sums
//   what those cards each show alone.
//
// THE CHART IS THE DESIGN SYSTEM'S. @astryxdesign/charts is already a
// dependency (E15's adoption); `bar` draws the one series the quota surface
// answers today and `referenceLine` draws the cap the same answer names. When
// E23 t1 lands a stored history, this bar-per-KB view gains the design's
// area-over-time meters WITHOUT this page changing shape: the rollup stays,
// the series swap.
import { useEffect, useState } from 'react';

import { Card } from '@astryxdesign/core/Card';
import { Grid } from '@astryxdesign/core/Grid';
import { Heading } from '@astryxdesign/core/Heading';
import { Link } from '@astryxdesign/core/Link';
import { Spinner } from '@astryxdesign/core/Spinner';
import { Stack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { Chart, ChartAxis, ChartGrid, bar, referenceLine } from '@astryxdesign/charts';

import type { KbRow, KbUsage, KbsClient } from '../api/kbs';
import { humanBytes, humanCount } from '../format/units';

/** One KB's usage as this page holds it: the answer, or the fact it never came.
 *  `null` is "the read failed" — NOT zero, and never summed as zero. */
export interface UsageCell {
  kb: KbRow;
  usage: KbUsage | null;
}

export interface BillingPageProps {
  org: string;
  client: KbsClient;
}

/** The rollup, over the cells that ANSWERED. `partial` is the honesty bit: a
 *  sum with holes must say so next to the number, not in a tooltip. */
export function rollup(cells: readonly UsageCell[]): {
  bytes: number;
  files: number;
  answered: number;
  partial: boolean;
} {
  let bytes = 0;
  let files = 0;
  let answered = 0;
  for (const cell of cells) {
    if (cell.usage === null) continue;
    bytes += cell.usage.bytes;
    files += cell.usage.files;
    answered += 1;
  }
  return { bytes, files, answered, partial: answered !== cells.length };
}

const MB = 1024 * 1024;

export function BillingPage({ org, client }: BillingPageProps) {
  const [cells, setCells] = useState<UsageCell[] | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setCells(null);
    setRefusal(null);
    void (async () => {
      try {
        const kbs = (await client.listKbs()).filter((kb) => kb.org === org);
        // One read per KB, settled together but failing apart (Billing f1):
        // Promise.all over per-cell catches, so ONE unreachable Durable Object
        // costs one unknown row and not the page.
        const settled = await Promise.all(
          kbs.map(
            async (kb): Promise<UsageCell> => ({
              kb,
              usage: await client.loadUsage(kb.org, kb.name).catch(() => null),
            }),
          ),
        );
        if (live) setCells(settled);
      } catch (error) {
        // The LIST failing is different from a cell failing: there is nothing
        // to roll up, and the server's own sentence is the one to show.
        if (live) setRefusal(error instanceof Error ? error.message : String(error));
      }
    })();
    return () => {
      live = false;
    };
  }, [client, org]);

  if (refusal !== null) {
    return (
      <Stack direction="vertical" gap={3} data-page="billing">
        <Heading level={1}>Usage &amp; billing</Heading>
        <Text type="body" color="secondary">
          {refusal}
        </Text>
      </Stack>
    );
  }

  if (cells === null) {
    return (
      <Stack direction="vertical" gap={3} vAlign="center" data-page="billing">
        <Spinner label="Reading usage" />
      </Stack>
    );
  }

  const totals = rollup(cells);
  // The cap is the ANSWER's (Billing f2). Every KB answers with the same
  // platform caps today; the first answered cell carries them, and no answered
  // cell means no line rather than a line from an import.
  const cap = cells.find((cell) => cell.usage?.limits)?.usage?.limits ?? null;
  const chartRows = cells
    .filter((cell) => cell.usage !== null)
    .map((cell) => ({
      kb: cell.kb.name,
      mb: Math.round(((cell.usage as KbUsage).bytes / MB) * 100) / 100,
    }));

  return (
    <Stack direction="vertical" gap={5} data-page="billing">
      <Stack direction="vertical" gap={1}>
        <Heading level={1}>Usage &amp; billing</Heading>
        <Text type="body" color="secondary">
          Every knowledge base in {org}, read from the same quota surface the forge enforces
          pushes against.
        </Text>
      </Stack>

      {/* f1 — the rollup, with its holes stated beside the numbers. */}
      <Grid columns={{ minWidth: 192, repeat: 'fit' }} gap={3}>
        <Card padding={4} elevation="none">
          <Stack direction="vertical" gap={1}>
            <Text type="label" color="secondary">
              Storage
            </Text>
            <Heading level={2}>{humanBytes(totals.bytes)}</Heading>
            {totals.partial ? (
              <Text type="supporting" color="secondary">
                {totals.answered} of {cells.length} knowledge bases answered — the total is
                missing the rest.
              </Text>
            ) : null}
          </Stack>
        </Card>
        <Card padding={4} elevation="none">
          <Stack direction="vertical" gap={1}>
            <Text type="label" color="secondary">
              Files
            </Text>
            <Heading level={2}>{humanCount(totals.files)}</Heading>
          </Stack>
        </Card>
        <Card padding={4} elevation="none">
          <Stack direction="vertical" gap={1}>
            <Text type="label" color="secondary">
              Knowledge bases
            </Text>
            <Heading level={2}>{humanCount(cells.length)}</Heading>
          </Stack>
        </Card>
      </Grid>

      {/* f2 — storage by KB against the cap the usage answer itself names. */}
      {chartRows.length > 0 ? (
        <Card padding={4} elevation="none">
          <Stack direction="vertical" gap={3}>
            <Stack direction="vertical" gap={1}>
              <Text type="label" weight="semibold">
                Storage by knowledge base
              </Text>
              {cap ? (
                <Text type="supporting" color="secondary">
                  The line is the per-KB cap the quota surface answered with —{' '}
                  {humanBytes(cap.kbBytes)} and {humanCount(cap.kbFiles)} files per knowledge
                  base at this tier.
                </Text>
              ) : (
                <Text type="supporting" color="secondary">
                  The usage answers named no caps, so no cap line is drawn.
                </Text>
              )}
            </Stack>
            <Chart
              data={chartRows}
              xKey="kb"
              height={220}
              series={[
                bar('mb'),
                ...(cap ? [referenceLine({ y: cap.kbBytes / MB, label: 'Per-KB cap' })] : []),
              ]}
              grid={<ChartGrid />}
              axes={
                <>
                  <ChartAxis position="bottom" />
                  <ChartAxis position="left" tickFormat={(value: unknown) => `${String(value)} MB`} />
                </>
              }
            />
          </Stack>
        </Card>
      ) : null}

      {/* f1's other half — the rows, unknowns included, each linking to the
          file-level ledger the Admin page already holds. */}
      <Stack direction="vertical" gap={2}>
        {cells.map((cell) => (
          <Card key={cell.kb.id} padding={3} elevation="none">
            <Stack direction="horizontal" gap={3} vAlign="center" wrap="wrap">
              <Text type="label" weight="semibold">
                {cell.kb.name}
              </Text>
              {cell.usage === null ? (
                <Text type="supporting" color="secondary">
                  usage unknown — the knowledge base did not answer
                </Text>
              ) : (
                <Text type="supporting" color="secondary">
                  {humanBytes(cell.usage.bytes)} · {humanCount(cell.usage.files)} files
                </Text>
              )}
              <Link href={`/${org}/settings`} type="label" color="secondary" isStandalone>
                File ledger
              </Link>
            </Stack>
          </Card>
        ))}
      </Stack>

      {/* f4 — the unbuilt majority, named rather than mocked. */}
      <Text type="supporting" color="secondary">
        No usage history is stored yet, so there are no meters over time; plans, agent-token
        metering and invoices are unbuilt. E23 t1, t3 and t4 build them, shaped by the answered
        pricing questions.
      </Text>
    </Stack>
  );
}

export default BillingPage;
